// @flow
import BeEndpoints from '../../services/end-points/be-endpoints';
import type { Request, ResponseState } from '../services/useRequest';

const CheckoutApiErrorCodes = {
  AUTHENTICATION_FAILURE: 1401,
  INVALID_MERCHANT: 3000,
  MERCHANT_NOT_SUPPORTED_BY_LENDER: 3005,
  INVALID_MOBILE_NUMBER: 3010,
  TXN_AMT_LESS_THAN_MIN_AMT: 3015,
  TXN_AMT_MORE_THAN_MAX_AMT: 3020,
  INVALID_ORDER: 3025,
  ORDER_TIMEOUT: 3030,
  ORDER_DETAILS_ATTEMPTS_BLOCKED: 3035,
  SERVER_ERROR: 1500,
  INVALID_OTP: 3040,
  LENDER_OTP_ATTEMPTS_BLOCKED: 3045,
  INVALID_AIVF: 3050,
  AIVF_ATTEMPTS_BLOCKED: 3055,
  USER_BLOCKED: 3060,
  INVALID_USER: 3065,
  USER_NOT_ELIGIBLE: 3070,
  USER_BLOCKED_BY_LENDER: 3075,
  ACCOUNT_NOT_ACTIVE: 3080,
  DUPLICATE_ORDER_REQUEST: 3085,
  DUPLICATE_TRANSACTION_REQUEST: 3090,
  CONCURRENT_TXN_ERROR: 3095,
  INSUFFICIENT_LIMIT: 3100,
  LENDER_ISSUE: 3105,
  INVALID_EMI_AMOUNT: 3110,
  INVALID_EMI_TENURE: 3115,
  RISK_REJECT: 3120,
  DEFAULT: 4000,
  INVALID_REQUEST_BODY: 1400,
  INVALID_CREDENTIALS: 3125,
  CUSTOMER_CANCELLED: 3130,
};

Object.freeze(CheckoutApiErrorCodes);

export { CheckoutApiErrorCodes };

export type EMIInfoType = {
  success: boolean,
  loanDuration: number,
  interestRate: number,
  monthlyInstallment: number,
  totalInterest: number,
  preEmi: number,
  effectiveInterestRate: number,
  effectiveMonthlyInstallment: number,
  effectivePreEmi: number,
  effectiveTotalInterest: number,
  transactionSettlementAmount: number,
  transactionAmount: number,
  loanAmount: number,
  discountType: string,
  totalDiscountAmount: number,
  flexmoneyDiscountAmount: number,
  creditSchemeVariantId: number,
  isSubventionEnabled: boolean,
  tenureType: 'DAYS' | 'MONTHS',
  emiSchedule: number,
};

export type LenderDetailsType = {
  lenderId: number,
  logo: string,
  logoIcon: string,
  lenderName: string,
  lenderType: 'EMI' | 'PAY_LATER',
  creditLineStatus: string,
  aivfType:
    | 'NONE'
    | 'LAST_DIGITS_OF_DEBIT_CARD'
    | 'PAN_CARD_NUMBERS'
    | 'PAN_CARD',
  emiDetailsList: EMIInfoType,
  emiDates: number[],
  emiNotes: string[],
  lenderTncUrl: string,
  emiDiscountType: string,
  aivfDetails: { [key: string]: string },
};

export type NtbLenderDetails = {
  lenderId: number,
  logo: string,
  lenderLogoIcon: string,
  coloredBackgroundLogo: string,
  lenderName: string,
  lenderTncUrl: string,
  maxLoanLimit: number,
  minInterestRate: number,
  hasPendingApplication: boolean,
  applicationType: 'NTB_INSTACRED_HOSTED' | 'NTB_LENDER_HOSTED',
  averageCompletionTime: number,
  creditApplicationProcessingFees: number,
  txnProcessingFessInPercentage: number,
};

type CallbackDataType = {
  payload: string,
  signature: string,
};

export type OrderDetailsResponseType = {
  orderDetails?: {
    orderAmount: number,
    partnerOrderId: number,
    merchantName: string,
    merchantLogo: string,
    channel: string,
    txnRemainingTimeInSeconds: number,
  },
  eligibilityDetails?: {
    userStatus: string,
    isEligible: boolean,
    isNTBAllowedOnMerchant: boolean,
    mobile: string,
    emiLenderDetailsList: LenderDetailsType[],
    payLaterLenderDetailsList: LenderDetailsType[],
  },
  callbackData?: CallbackDataType,
};

export type AuthorizeRequestType = {
  authValue: string,
  lenderId: string,
  tenure: string,
  paymentCategory: string,
};

export type AuthorizeResponseType = {
  instaCredTxnId: string,
  message: string,
};

export type GetEligibleNTBLendersRequestType = {
  loanEligibilityAnswerMap: any,
  merchantId: number,
};

export type GetEligibleNTBLendersResponseType = {
  lenderDetailsList: NtbLenderDetails[],
};

export type InitNtbApplicationRequestType = {
  lenderId: number,
  merchantId: number,
};

export type InitNtbApplicationResponseType = {
  applicationId: string,
  redirectUrl: string,
};

export type NtbStatusAPIResponseType = {
  creditApplicationStatus: string,
  lenderCreditApplicationStatus: string,
  remark: string,
  approvalLimit: string,
  isLenderHostedTransactionJourney: boolean,
};

export type PaymentConfirmResponseType = {
  authToken: string,
  txnStatus: string,
  callbackData?: CallbackDataType,
};

export type CancelOrderResponseType = {
  orderStatus: string,
  callbackData: CallbackDataType,
};

export type GetKfsRequestType = {
  txnUuid: string,
  lenderId: string,
  emiTenure: string,
};

export type KfsDataSectionType = {
  title?: string,
  sectionType: string,
  sectionData: any[],
};

export type GetKfsResponseType = {
  kfsEnabled: string,
  kfsData: KfsDataSectionType[],
  txnDetails: TxnDetailsType,
};

export type TxnDetailsType = {
  txnStatus: string,
};

export type ErrorResponseType = {
  errorCode: number,
  title: string,
  message: string,
  callbackData?: CallbackDataType,
  isOrderRetryable?: boolean,
  txnStatus?: string,
};

const getOrderDetailsAPI = (
  makeRequest: (
    requestConfig: Request<{ mobile?: string }>,
  ) => Promise<ResponseState<OrderDetailsResponseType>>,
  mobile?: string,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_GET_ORDER_DETAILS}`,
    method: 'POST',
    data: {
      mobile,
    },
  };
  return makeRequest(request);
};

const sendOtpAPI = (
  makeRequest: (
    requestConfig: Request<{ mobile: string }>,
  ) => Promise<ResponseState<>>,
  mobile: string,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_SEND_OTP}`,
    method: 'POST',
    data: {
      mobile,
    },
  };
  return makeRequest(request);
};

const verifyOtpAPI = (
  makeRequest: (
    requestConfig: Request<{ mobile: string, otp: string }>,
  ) => Promise<ResponseState<>>,
  mobile: string,
  otp: string,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_VERIFY_OTP}`,
    method: 'POST',
    data: {
      mobile,
      otp,
    },
  };
  return makeRequest(request);
};

const paymentAuthorizeAPI = (
  makeRequest: (
    requestConfig: Request<AuthorizeRequestType>,
  ) => Promise<ResponseState<AuthorizeResponseType>>,
  authorizeApiData: AuthorizeRequestType,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_PAYMENT_AUTHORIZE}`,
    method: 'POST',
    data: authorizeApiData,
  };
  return makeRequest(request);
};

const paymentConfirmAPI = (
  makeRequest: (
    requestConfig: Request<{ otp: string }>,
  ) => Promise<ResponseState<PaymentConfirmResponseType>>,
  otp: string,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_PAYMENT_CONFIRM}`,
    method: 'POST',
    data: {
      otp,
    },
  };
  return makeRequest(request);
};
const resendOtpAPI = (
  makeRequest: (requestConfig: Request<>) => Promise<ResponseState<>>,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_RESEND_OTP}`,
    method: 'POST',
  };
  return makeRequest(request);
};

const cancelOrderAPI = (
  makeRequest: (
    requestConfig: Request<>,
  ) => Promise<ResponseState<CancelOrderResponseType>>,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_ORDER_CANCEL}`,
    method: 'POST',
  };
  return makeRequest(request);
};

const getKfsDataAPI = (
  makeRequest: (
    requestConfig: Request<GetKfsRequestType>,
  ) => Promise<ResponseState<GetKfsResponseType>>,
  getKfsRequestParams: GetKfsRequestType,
) => {
  const request = {
    path: `${BeEndpoints.CHECKOUT_GET_KFS_DATA}`,
    method: 'GET',
    params: getKfsRequestParams,
  };
  return makeRequest(request);
};

const cwGetNtbLenderListAPI = (
  makeRequest: (
    requestConfig: Request<GetEligibleNTBLendersRequestType>,
  ) => Promise<ResponseState<GetEligibleNTBLendersResponseType>>,
  getNtbEligibleLendersApiData: GetEligibleNTBLendersRequestType,
) => {
  const request = {
    path: `${BeEndpoints.CW_NTB_LENDER_LIST}`,
    method: 'POST',
    data: getNtbEligibleLendersApiData,
  };
  return makeRequest(request);
};

const cwGetNtbLenderStatusAPI = (
  makeRequest: (
    requestConfig: Request<{ applicationId: string }>,
  ) => Promise<ResponseState<NtbStatusAPIResponseType>>,
  applicationId: string,
) => {
  const request = {
    path: `${BeEndpoints.CW_NTB_LENDER_STATUS}`,
    method: 'POST',
    data: { applicationId },
  };
  return makeRequest(request);
};

const cwInitiateLenderNTBAPI = (
  makeRequest: (
    requestConfig: Request<InitNtbApplicationRequestType>,
  ) => Promise<ResponseState<InitNtbApplicationResponseType>>,
  getInitNtbApplicationApiData: InitNtbApplicationRequestType,
) => {
  const request = {
    path: `${BeEndpoints.CW_INITIATE_LENDER_NTB}`,
    method: 'POST',
    data: getInitNtbApplicationApiData,
  };
  return makeRequest(request);
};

export {
  getOrderDetailsAPI,
  sendOtpAPI,
  verifyOtpAPI,
  paymentAuthorizeAPI,
  paymentConfirmAPI,
  resendOtpAPI,
  cancelOrderAPI,
  getKfsDataAPI,
  cwGetNtbLenderListAPI,
  cwInitiateLenderNTBAPI,
  cwGetNtbLenderStatusAPI,
};



// WEBPACK FOOTER //
// ./api/controllers/checkoutController.js